import React, {useContext, useEffect, useMemo, useState} from 'react';
import {observer} from "mobx-react-lite";
import {Link} from "react-router-dom";
import {Context} from "../index";
import NavPanel from "../components/NavPanel";
import BackButton from "../components/BackButton";
import {URLS} from "../utils/urls";
import {WsTriggers} from "../WebsocketTriggers";
import {getUserHighestRole} from "../utils/userRoles";
import {companyGetAPI} from "../http/companyAPI";
import {
    groupsGetApi,
    groupCreateApi,
    groupUpdateApi,
    groupDeleteApi,
    groupAddCompanyApi,
    groupRemoveCompanyApi,
} from "../http/groupAPI";
import '../styles/CompanyGroups.css';


const DEFAULT_GROUP_COLOR = '#4f8ef7';

type GroupCompany = { id: number, name: string };
type GroupType = {
    id: number,
    name: string,
    color: string | null,
    ownerId: number | null,
    owner: { id: number, username: string } | null,
    companies: Array<GroupCompany>,
};


const CompanyGroups = observer(() => {
    const { user, ws } = useContext(Context);

    const isAdmin = getUserHighestRole() === 'admin';
    const currentUserId = Number(user.getUser.id);

    const [groups, setGroups] = useState<Array<GroupType>>([]);
    const [companies, setCompanies] = useState<Array<GroupCompany>>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const [newName, setNewName] = useState<string>('');
    const [newColor, setNewColor] = useState<string>(DEFAULT_GROUP_COLOR);

    const [editName, setEditName] = useState<string>('');
    const [editColor, setEditColor] = useState<string>(DEFAULT_GROUP_COLOR);

    const [companyQuery, setCompanyQuery] = useState<string>('');
    const [busy, setBusy] = useState<boolean>(false);

    const requestGroups = () => {
        groupsGetApi()
            .then((res) => { if (Array.isArray(res?.data)) setGroups(res.data); })
            .catch((err) => { console.log(err); });
    };

    useEffect(() => {
        requestGroups();
        companyGetAPI()
            .then((res) => {
                if (res?.data?.companies) {
                    setCompanies(res.data.companies.map((c: any) => ({ id: c.id, name: c.name })));
                }
            })
            .catch((err) => { console.log(err); });
    }, []);

    useEffect(() => {
        const msg = ws.getMsg;
        if (msg === WsTriggers.GROUPS || msg === WsTriggers.COMPANIES) {
            requestGroups();
            ws.setMessage(null);
        }
    }, [ws.getMsg]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedGroup = useMemo(
        () => groups.find((g) => g.id === selectedId) ?? null,
        [groups, selectedId],
    );

    // Seed the edit fields whenever the selected group (or its data) changes.
    useEffect(() => {
        if (selectedGroup) {
            setEditName(selectedGroup.name);
            setEditColor(selectedGroup.color || DEFAULT_GROUP_COLOR);
            setCompanyQuery('');
        }
    }, [selectedGroup?.id, selectedGroup?.name, selectedGroup?.color]); // eslint-disable-line react-hooks/exhaustive-deps

    const canManage = (g: GroupType | null): boolean =>
        !!g && (isAdmin || (g.ownerId != null && g.ownerId === currentUserId));

    const handleCreate = (e: React.FormEvent) => {
        e.preventDefault();
        const name = newName.trim();
        if (!name || busy) return;
        setBusy(true);
        groupCreateApi({ name, color: newColor })
            .then((res) => {
                if (res?.data?.id) {
                    setNewName('');
                    setNewColor(DEFAULT_GROUP_COLOR);
                    setSelectedId(res.data.id);
                    requestGroups();
                }
            })
            .catch((err) => { console.log(err); })
            .finally(() => setBusy(false));
    };

    const handleSaveEdit = () => {
        if (!selectedGroup || !canManage(selectedGroup) || busy) return;
        const name = editName.trim();
        if (!name) return;
        setBusy(true);
        groupUpdateApi(selectedGroup.id, { name, color: editColor })
            .then(() => requestGroups())
            .catch((err) => { console.log(err); })
            .finally(() => setBusy(false));
    };

    const handleDelete = () => {
        if (!selectedGroup || !canManage(selectedGroup) || busy) return;
        if (!window.confirm(`Удалить группу «${selectedGroup.name}»?`)) return;
        setBusy(true);
        groupDeleteApi(selectedGroup.id)
            .then(() => { setSelectedId(null); requestGroups(); })
            .catch((err) => { console.log(err); })
            .finally(() => setBusy(false));
    };

    const handleAddCompany = (companyId: number) => {
        if (!selectedGroup || !canManage(selectedGroup) || busy) return;
        setBusy(true);
        groupAddCompanyApi(selectedGroup.id, companyId)
            .then(() => requestGroups())
            .catch((err) => { console.log(err); })
            .finally(() => setBusy(false));
    };

    const handleRemoveCompany = (companyId: number) => {
        if (!selectedGroup || !canManage(selectedGroup) || busy) return;
        setBusy(true);
        groupRemoveCompanyApi(selectedGroup.id, companyId)
            .then(() => requestGroups())
            .catch((err) => { console.log(err); })
            .finally(() => setBusy(false));
    };

    const memberIds = new Set((selectedGroup?.companies ?? []).map((c) => c.id));
    const addableCompanies = companies
        .filter((c) => !memberIds.has(c.id))
        .filter((c) => c.name?.toLowerCase().includes(companyQuery.trim().toLowerCase()))
        .slice(0, 30);

    const manageable = canManage(selectedGroup);

    return (
        <div className='companyGroups page-content-container'>
            <NavPanel/>
            <div className='companyGroups__body page_content_body'>
                <div className='companyGroups__page-header'>
                    <BackButton to={URLS.COMPANIES_URL} label='К предприятиям'/>
                    <div>
                        <div className='companyGroups__page-title'>Группы предприятий</div>
                        <div className='companyGroups__page-subtitle'>{groups.length} групп</div>
                    </div>
                </div>

                <div className='companyGroups__layout'>
                    <aside className='companyGroups__list contentWindow'>
                        <form className='companyGroups__create' onSubmit={handleCreate}>
                            <input className='input companyGroups__create-name'
                                   placeholder='Новая группа'
                                   maxLength={128}
                                   value={newName}
                                   onChange={(e) => setNewName(e.target.value)}/>
                            <input className='input companyGroups__color'
                                   type='color'
                                   value={newColor}
                                   onChange={(e) => setNewColor(e.target.value)}
                                   title='Цвет группы'/>
                            <button type='submit'
                                    className='nav__button btn-primary'
                                    disabled={busy || newName.trim() === ''}>Создать</button>
                        </form>

                        <div className='companyGroups__list-body'>
                            {
                                groups.length === 0 ?
                                    <div className='companyGroups__empty'>Групп пока нет</div>
                                    :
                                    groups.map((g) => (
                                        <button type='button'
                                                key={`group-${g.id}`}
                                                className={'companyGroups__list-row' + (g.id === selectedId ? ' is-active' : '')}
                                                onClick={() => setSelectedId(g.id)}>
                                            <span className='companyGroups__dot'
                                                  style={{ backgroundColor: g.color || DEFAULT_GROUP_COLOR }}/>
                                            <span className='companyGroups__list-name'>{g.name}</span>
                                            <span className='companyGroups__list-meta'>
                                                {g.companies.length} · {g.owner ? g.owner.username : '—'}
                                            </span>
                                        </button>
                                    ))
                            }
                        </div>
                    </aside>

                    <section className='companyGroups__detail contentWindow'>
                        {
                            !selectedGroup ?
                                <div className='companyGroups__empty'>Выберите группу слева</div>
                                :
                                <>
                                    <div className='companyGroups__detail-header'>
                                        {
                                            manageable ?
                                                <>
                                                    <input className='input companyGroups__color'
                                                           type='color'
                                                           value={editColor}
                                                           onChange={(e) => setEditColor(e.target.value)}
                                                           title='Цвет группы'/>
                                                    <input className='input companyGroups__detail-name'
                                                           maxLength={128}
                                                           value={editName}
                                                           onChange={(e) => setEditName(e.target.value)}/>
                                                    <button className='nav__button btn-primary'
                                                            onClick={handleSaveEdit}
                                                            disabled={busy || editName.trim() === ''}>Сохранить</button>
                                                    <button className='nav__button btn-danger'
                                                            onClick={handleDelete}
                                                            disabled={busy}>Удалить</button>
                                                </>
                                                :
                                                <>
                                                    <span className='companyGroups__dot'
                                                          style={{ backgroundColor: selectedGroup.color || DEFAULT_GROUP_COLOR }}/>
                                                    <span className='companyGroups__detail-title'>{selectedGroup.name}</span>
                                                    <span className='companyGroups__detail-owner'>
                                                        владелец: {selectedGroup.owner ? selectedGroup.owner.username : '—'}
                                                    </span>
                                                </>
                                        }
                                    </div>

                                    <div className='companyGroups__members'>
                                        <div className='companyGroups__section-title'>Предприятия в группе ({selectedGroup.companies.length})</div>
                                        {
                                            selectedGroup.companies.length === 0 ?
                                                <div className='companyGroups__empty'>Пусто</div>
                                                :
                                                <ul className='companyGroups__member-list'>
                                                    {selectedGroup.companies.map((c) => (
                                                        <li key={`member-${c.id}`} className='companyGroups__member'>
                                                            <Link className='companyGroups__member-link'
                                                                  to={URLS.COMPANY_URL.replace(':id', `${c.id}`)}>{c.name}</Link>
                                                            {
                                                                manageable &&
                                                                <button type='button'
                                                                        className='companyGroups__member-remove'
                                                                        title='Убрать из группы'
                                                                        onClick={() => handleRemoveCompany(c.id)}
                                                                        disabled={busy}>×</button>
                                                            }
                                                        </li>
                                                    ))}
                                                </ul>
                                        }
                                    </div>

                                    {
                                        manageable &&
                                        <div className='companyGroups__add'>
                                            <div className='companyGroups__section-title'>Добавить предприятие</div>
                                            <input className='input companyGroups__add-search'
                                                   placeholder='Поиск предприятия'
                                                   value={companyQuery}
                                                   onChange={(e) => setCompanyQuery(e.target.value)}/>
                                            <ul className='companyGroups__add-list'>
                                                {
                                                    addableCompanies.length === 0 ?
                                                        <li className='companyGroups__empty'>Ничего не найдено</li>
                                                        :
                                                        addableCompanies.map((c) => (
                                                            <li key={`add-${c.id}`} className='companyGroups__add-row'>
                                                                <span>{c.name}</span>
                                                                <button type='button'
                                                                        className='companyGroups__add-btn'
                                                                        onClick={() => handleAddCompany(c.id)}
                                                                        disabled={busy}>+</button>
                                                            </li>
                                                        ))
                                                }
                                            </ul>
                                        </div>
                                    }
                                </>
                        }
                    </section>
                </div>
            </div>
        </div>
    );
});


export default CompanyGroups;
